import { useState, useEffect, useCallback } from "react";
import { Megaphone, Loader2, CheckCircle, Wand2, Sparkles, PlusCircle, Trash2, RotateCcw, Users, Zap, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import AutoResizeTextarea from "@/components/AutoResizeTextarea";
import StrategyTabBar from "./StrategyTabBar";
import ClusteredTodoTab from "./ClusteredTodoTab";
import { useStrategyModule } from "@/hooks/useStrategyModule";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

const VALID_TABS = new Set(["narrative", "compression", "standout", "todos"]);
const SUB_TOPICS = [
  { key: "narrative",   label: "Narrative & Audience" },
  { key: "compression", label: "Compression Ladder" },
  { key: "standout",    label: "Stand-Out" },
];
const HEADERS = {
  narrative:   { title: "Narrative & Audience", subtitle: "Your core message, then a tailored version per audience — because a Series A partner and a KOL will not hear the same story the same way." },
  compression: { title: "Compression Ladder",   subtitle: "The same story compressed 3 ways: 4-6 words, one sentence, one paragraph. If you can't get shorter, you don't understand it yet." },
  standout:    { title: "Stand-Out",            subtitle: "How you don't sound like every other biotech pitch. Brainstorm concrete, actionable angles." },
  todos:       { title: "To Do",                subtitle: "Top 5 priorities across the three topics. Generate a Framing overview when ready." },
};

const AUDIENCE_OPTIONS = [
  "Seed investor", "Series A investor", "Pharma BD",
  "Clinical KOL", "Patient / advocacy group", "Grant reviewer",
  "General audience", "Board / hiring candidate",
];

const EMPTY = {
  narrative: {
    core_message: "", why_now: "", why_us: "",
    pharma_message:   { hook: "", promise: "", proof: "", ask: "" },
    investor_message: { hook: "", promise: "", proof: "", ask: "" },
    guidance: [],
  },
  compression: { four_to_six_words: "", one_sentence: "", one_paragraph: "", guidance: [] },
  standout: {
    differentiator: "", unfair_advantage: "", polarising_take: "", memorable_analogy: "",
    brainstorm_notes: "", guidance: [],
  },
};

// ---------- Shared wand atoms ----------
function FieldWand({ tab, field, fieldLabel, current, context, onAnswer, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/framing/suggest-field`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab, field, fieldLabel, current, context }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.answer) { onAnswer(d.answer); toast.success("AI draft ready"); }
        else toast.info("No draft");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  return (
    <Button size="sm" variant="ghost" onClick={run} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={testid}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
      <span className="ml-1 text-xs">AI draft</span>
    </Button>
  );
}

function WandField({ tab, field, label, value, onChange, onBlur, rows = 2, placeholder, context, testid, maxH = 400 }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-slate-800">{label}</Label>
        <FieldWand tab={tab} field={field} fieldLabel={label} current={value} context={context} onAnswer={(a) => onChange(a)} testid={`${testid}-wand`} />
      </div>
      <AutoResizeTextarea value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} minRows={rows} maxHeight={maxH} className="mt-1 text-sm leading-relaxed" placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

// ---------- Narrative & Audience tab ----------
function MessageCard({ title, subtitle, msgKey, msg, accent, onChange, onCommit }) {
  return (
    <Card className={`border-l-4 ${accent}`} data-testid={`fn-msg-${msgKey}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-slate-800">{title}</CardTitle>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {["hook", "promise", "proof", "ask"].map((k) => (
          <div key={k}>
            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">{k}</Label>
            <AutoResizeTextarea
              value={msg[k] || ""}
              onChange={(e) => onChange(msgKey, { [k]: e.target.value })}
              onBlur={onCommit}
              minRows={1}
              maxHeight={200}
              className="text-xs mt-0.5"
              placeholder={k === "hook" ? "First-breath attention grab" : k === "promise" ? "What changes for them" : k === "proof" ? "Most credible piece of evidence" : "The specific next step"}
              data-testid={`fn-msg-${msgKey}-${k}`}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function NarrativeTab({ data, setData, persist }) {
  const set = (patch) => setData({ ...data, ...patch });
  const setField = (k) => (v) => set({ [k]: v });
  const persistTab = (next) => persist({ narrative: next });
  const ctx = { core_message: data.core_message, why_now: data.why_now, why_us: data.why_us };

  const [msgBusy, setMsgBusy] = useState(false);
  const [whyNowBusy, setWhyNowBusy] = useState(false);
  const pharma   = data.pharma_message   || { hook: "", promise: "", proof: "", ask: "" };
  const investor = data.investor_message || { hook: "", promise: "", proof: "", ask: "" };

  // Wand for Why Now — first refresh Market news (so the AI has live context), then draft.
  const draftWhyNow = async () => {
    setWhyNowBusy(true);
    try {
      // Ensure market news is generated (1h cache handled server-side).
      try {
        await fetch(`${BACKEND_URL}/api/strategy/market/market-news`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({}),
        });
      } catch (e) { /* non-blocking */ }
      // Then draft the why-now anchored on those trends.
      const res = await fetch(`${BACKEND_URL}/api/strategy/framing/suggest-field`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab: "narrative", field: "why_now", fieldLabel: "Why now", current: data.why_now || "", context: ctx }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.answer) { setField("why_now")(d.answer); toast.success("Why-now anchored on latest market news"); }
        else toast.info("No draft");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setWhyNowBusy(false);
  };

  const draftBothMessages = async () => {
    setMsgBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/framing/suggest-audience-messages`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const d = await res.json();
        const next = { ...data, pharma_message: d.pharma || pharma, investor_message: d.investor || investor };
        setData(next); persistTab(next);
        toast.success("Pharma + Investor messages drafted");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setMsgBusy(false);
  };

  const updateMsg = (which, patch) => {
    const next = { ...data, [which]: { ...(data[which] || {}), ...patch } };
    setData(next);
  };
  const commitMsg = () => persistTab(data);

  return (
    <div className="space-y-4" data-testid="framing-narrative">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Zap className="w-4 h-4 text-teal-600" /> A · Core narrative</CardTitle>
          <p className="text-xs text-slate-500">The single truth of what you&apos;re doing — before you translate it for anyone.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <WandField tab="narrative" field="core_message" label="Core message" value={data.core_message} onChange={setField("core_message")} onBlur={persist} placeholder="The one thing you want every reader to remember. Plain language." context={ctx} testid="fn-core" maxH={200} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-slate-800">Why now</Label>
                <Button size="sm" variant="ghost" onClick={draftWhyNow} disabled={whyNowBusy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid="fn-why-now-wand">
                  {whyNowBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                  <span className="ml-1 text-xs">AI draft (uses market news)</span>
                </Button>
              </div>
              <AutoResizeTextarea value={data.why_now || ""} onChange={(e) => setField("why_now")(e.target.value)} onBlur={persist} minRows={3} maxHeight={400} className="mt-1 text-sm leading-relaxed" placeholder="Why is this the right moment? AI draft will refresh Market news first and anchor the answer on real recent trends." data-testid="fn-why-now" />
            </div>
            <WandField tab="narrative" field="why_us" label="Why us" value={data.why_us} onChange={setField("why_us")} onBlur={persist} placeholder="Why are YOU (specifically named team members) uniquely positioned? AI draft names people from your Team tab / deck." context={ctx} testid="fn-why-us" rows={3} maxH={400} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Users className="w-4 h-4 text-teal-600" /> B · Tailored messages: Pharma vs Investor</CardTitle>
            <Button size="sm" onClick={draftBothMessages} disabled={msgBusy} className="bg-amber-600 hover:bg-amber-700 text-white h-8 px-3" data-testid="fn-msg-draft-both">
              {msgBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
              <span className="text-xs">Draft both messages</span>
            </Button>
          </div>
          <p className="text-xs text-slate-500">Same core story, two audiences. Pharma anchors on differentiators vs past failures or riding a recent success. Investor anchors on market timing, capital efficiency and exit.</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <MessageCard
            title="For Pharma BD"
            subtitle="Differentiator vs prior-modality failures OR riding a recent breakthrough."
            msgKey="pharma_message"
            msg={pharma}
            accent="border-l-cyan-500"
            onChange={updateMsg}
            onCommit={commitMsg}
          />
          <MessageCard
            title="For Investors"
            subtitle="Market timing, value-inflection to next round, capital efficiency, exit."
            msgKey="investor_message"
            msg={investor}
            accent="border-l-amber-500"
            onChange={updateMsg}
            onCommit={commitMsg}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Compression Ladder tab ----------
function CompressionTab({ data, setData, persist }) {
  const set = (patch) => setData({ ...data, ...patch });
  const setField = (k) => (v) => set({ [k]: v });
  const ctx = { four_to_six_words: data.four_to_six_words, one_sentence: data.one_sentence, one_paragraph: data.one_paragraph };
  return (
    <div className="space-y-4" data-testid="framing-compression">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Zap className="w-4 h-4 text-teal-600" /> The compression ladder</CardTitle>
          <p className="text-xs text-slate-500">Draft the paragraph first, then compress. Or compress first, then expand. Either way — all three should feel like the same story.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <WandField tab="compression" field="four_to_six_words" label="4-6 words (Twitter bio)" value={data.four_to_six_words} onChange={setField("four_to_six_words")} onBlur={persist} rows={1} placeholder="e.g. AAV gene therapy for DMD" context={ctx} testid="fc-4to6" maxH={100} />
          <WandField tab="compression" field="one_sentence" label="One sentence (max ~25 words)" value={data.one_sentence} onChange={setField("one_sentence")} onBlur={persist} rows={2} placeholder="e.g. We help X do Y so that Z." context={ctx} testid="fc-1sent" maxH={200} />
          <WandField tab="compression" field="one_paragraph" label="One paragraph (3-4 sentences)" value={data.one_paragraph} onChange={setField("one_paragraph")} onBlur={persist} rows={4} placeholder="The email-to-a-friend version. Sets up the problem, the mechanism, the ask." context={ctx} testid="fc-1para" maxH={400} />
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Stand-Out brainstorm tab ----------
function StandoutTab({ data, setData, persist }) {
  const set = (patch) => setData({ ...data, ...patch });
  const setField = (k) => (v) => set({ [k]: v });
  const persistTab = (next) => persist({ standout: next });
  const [busy, setBusy] = useState(false);
  const angles = data.angles || [];
  const ctx = { differentiator: data.differentiator, unfair_advantage: data.unfair_advantage, polarising_take: data.polarising_take };

  const brainstorm = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/framing/suggest-standout`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ current_notes: data.brainstorm_notes || "" }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.angles?.length) {
          const next = { ...data, angles: d.angles };
          setData(next); persistTab(next);
          toast.success(`${d.angles.length} stand-out angles ready`);
        } else toast.info("No angles");
      } else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  const clearAngles = () => { const next = { ...data, angles: [] }; setData(next); persistTab(next); };

  return (
    <div className="space-y-4" data-testid="framing-standout">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Sparkles className="w-4 h-4 text-teal-600" /> A · Sharp questions</CardTitle>
          <p className="text-xs text-slate-500">Four questions that force you to be specific about what makes you different.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <WandField tab="standout" field="differentiator" label="If someone else could copy us in 6 months, what would we still have that they wouldn't?" value={data.differentiator} onChange={setField("differentiator")} onBlur={persist} placeholder="Your durable moat — data, network, IP, insight, speed." context={ctx} testid="fs-diff" />
            <WandField tab="standout" field="unfair_advantage" label="What's the unfair advantage we don't shout about but should?" value={data.unfair_advantage} onChange={setField("unfair_advantage")} onBlur={persist} placeholder="Founders often bury the most interesting thing about their company." context={ctx} testid="fs-unfair" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <WandField tab="standout" field="polarising_take" label="What's a polarising view we hold that most people in this space would disagree with?" value={data.polarising_take} onChange={setField("polarising_take")} onBlur={persist} placeholder="e.g. 'AAV is a dead end for DMD, we're building on lipid nanoparticles.'" context={ctx} testid="fs-polar" />
            <WandField tab="standout" field="memorable_analogy" label="What one analogy or reference makes what we do click instantly?" value={data.memorable_analogy} onChange={setField("memorable_analogy")} onBlur={persist} placeholder="e.g. 'The Tesla of gene therapy manufacturing' — but specific." context={ctx} testid="fs-analogy" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-teal-600" /> B · Brainstorm angles</CardTitle>
            <div className="flex items-center gap-1">
              {angles.length > 0 && (
                <Button size="sm" variant="ghost" onClick={clearAngles} className="h-7 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="fs-angles-clear">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={brainstorm} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid="fs-angles-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                <span className="ml-1 text-xs">{angles.length > 0 ? "Regenerate" : "Brainstorm"} angles</span>
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500">Paste raw notes below (optional) — then click Brainstorm to get 4-6 concrete angles you might not have considered.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <AutoResizeTextarea
            value={data.brainstorm_notes || ""}
            onChange={(e) => setField("brainstorm_notes")(e.target.value)}
            onBlur={persist}
            minRows={2}
            maxHeight={400}
            className="text-sm leading-relaxed"
            placeholder="Your rough thoughts about how you might stand out — free-text is fine, AI will build on it."
            data-testid="fs-notes"
          />
          {angles.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {angles.map((a) => (
                <div key={a.id} className="border rounded-lg bg-teal-50/40 border-teal-200 p-3" data-testid={`fs-angle-${a.id}`}>
                  <div className="flex items-start gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px] shrink-0 border-teal-300 text-teal-700">Angle</Badge>
                    <p className="text-sm font-medium text-slate-900 leading-snug">{a.title}</p>
                  </div>
                  <p className="text-xs text-slate-700 leading-relaxed">{a.body}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Main shell ----------
export default function FramingStrategy() {
  const shell = useStrategyModule({ endpointKey: "framing", defaultTab: "narrative", validTabs: VALID_TABS });
  const { user, companyProfile, loading, saving, activeTab, setActiveTab, urlTab, followups, setFollowups, fetchProgress, postProgress } = shell;

  const [narrative, setNarrative] = useState(EMPTY.narrative);
  const [compression, setCompression] = useState(EMPTY.compression);
  const [standout, setStandout] = useState(EMPTY.standout);

  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const d = await fetchProgress();
    if (!d) return;
    if (d.narrative)   setNarrative({ ...EMPTY.narrative, ...d.narrative });
    if (d.compression) setCompression({ ...EMPTY.compression, ...d.compression });
    if (d.standout)    setStandout({ ...EMPTY.standout, ...d.standout });
    setFollowups((d.followups || []).map((f) => ({ id: f.id || uid(), text: f.text || "", done: !!f.done, sub_module: f.sub_module || null })));
    if (d.activeView && VALID_TABS.has(d.activeView) && !urlTab) setActiveTab(d.activeView);
  };

  const persist = useCallback(async (overrides = {}, showToast = false) => {
    const payload = { narrative, compression, standout, followups, activeView: activeTab, ...overrides };
    await postProgress(payload, showToast);
  }, [narrative, compression, standout, followups, activeTab, postProgress]);

  const resetAll = () => {
    if (window.confirm("Reset all Framing content?")) {
      setNarrative(EMPTY.narrative); setCompression(EMPTY.compression); setStandout(EMPTY.standout);
      postProgress({ narrative: EMPTY.narrative, compression: EMPTY.compression, standout: EMPTY.standout, followups: [] });
      toast.success("Reset");
    }
  };

  if (loading) {
    return (
      <AppLayout user={user}>
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>
      </AppLayout>
    );
  }

  return (
    <AppLayout user={user}>
      <div className="max-w-[1400px]" data-testid="framing-strategy-page">
        <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center"><Megaphone className="w-5 h-5 text-teal-600" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{HEADERS[activeTab]?.title || "Framing & Storytelling"}</h1>
              <p className="text-sm text-slate-500">{HEADERS[activeTab]?.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={resetAll} className="text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="framing-reset-btn">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => persist({}, true)} data-testid="framing-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />} Save
            </Button>
          </div>
        </div>

        <StrategyTabBar
          tabs={SUB_TOPICS}
          activeTab={activeTab}
          onSelect={setActiveTab}
          todoCount={followups.filter((f) => !f.done).length}
        />

        <div data-testid={`framing-panel-${activeTab}`}>
          {activeTab === "narrative"   && <NarrativeTab data={narrative} setData={setNarrative} persist={persist} />}
          {activeTab === "compression" && <CompressionTab data={compression} setData={setCompression} persist={persist} />}
          {activeTab === "standout"    && <StandoutTab data={standout} setData={setStandout} persist={persist} />}
          {activeTab === "todos" && (
            <ClusteredTodoTab
              items={followups}
              setItems={setFollowups}
              persist={persist}
              endpointKey="framing"
              getSuggestBody={(existing) => ({
                progress: { narrative, compression, standout },
                existing,
              })}
              groupTitle="Framing & storytelling priorities"
            />
          )}
        </div>
      </div>
    </AppLayout>
  );
}
